import React, { useMemo, useEffect, useState, Fragment } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { getCurPatientId } from '@web/stores/Account/Select'
import testResultsActions from 'stores/TestResults/Actions'
import FhirEnhancedActions from 'stores/FhirEnhanced/Actions'
import { getStartDate, getEndDate } from 'stores/TestResults/Select'

import useMediaQuery from 'hooks/useMediaQuery'

import Loading from 'components/Loading'
import DatePicker from 'components/DatePicker'

import NoData from 'containers/MyHealth/components/MyHealthContentArea/NoDataAvailableBar'
import SthWentWrong from 'containers/MyHealth/components/MyHealthContentArea/SthWentWrong'

import ResultItem from './ResultItem'
import themeStyles from './styles'
import { useTheme } from 'contexts/ThemeProvider'
import localization from './localization'

import { groupTestResultsByDate, parseTestResults } from 'helpers/parseEnhanced'

import { Space } from 'antd'

import {
  allEncounters,
  allEncountersLoading
} from '@web/stores/Encounter/Select'
import FhirActions from '@enlyt/stores/Fhir/Actions'

import { Button } from 'semantic-ui-react'
import moment from 'moment'
import dayjs from 'dayjs'
import _ from 'lodash'
import {
  observationSearchNextLoading,
  observation,
  observationCache,
  observationSearchError
} from 'stores/FhirEnhanced/Select'
import LoadMoreButton from 'components/LoadMoreButton'
import queryParams, { getPageParamsFromUrl } from 'helpers/urlhelper'
import withLocalization from '@web/helpers/localizationUtils'

import Arrow from '@icons/icon-arrow-filter.svg'

const TestResults = ({ loading, data, name, t }) => {
  const { theme } = useTheme()
  const styles = themeStyles(theme)
  const dispatch = useDispatch()
  const patientId = useSelector(getCurPatientId)
  const [openedItemIndex, setOpenedItemIndex] = useState(null)
  const startDate = useSelector(getStartDate)
  const endDate = useSelector(getEndDate)
  const setStartDate = startDate =>
    dispatch(testResultsActions.setStartDate(startDate))
  const setEndDate = endDate => dispatch(testResultsActions.setEndDate(endDate))
  const groupedTests = useMemo(() => parseTestResults(data), [data])
  const { isMobile } = useMediaQuery()
  const testsInfoGroupedByDates = useMemo(
    () => groupTestResultsByDate(groupedTests),
    [groupedTests]
  )
  const arrEncounterId = useMemo(
    () =>
      testsInfoGroupedByDates.map(test => {
        const testInfo = Object.values(test)
        return _.get(testInfo, '[0][0].encounterId', '')
      }),
    [testsInfoGroupedByDates]
  )
  const uniqEncounterId = useMemo(
    () => _.uniq(arrEncounterId),
    [arrEncounterId]
  )
  const encounter = useSelector(allEncounters)
  const encounterLoading = useSelector(allEncountersLoading)
  const cachedTestResults = useSelector(observationCache)

  // Pagination config
  const observationData = useSelector(observation)
  const isObservationNextLoading = useSelector(observationSearchNextLoading)
  const [allDataLoaded, setAllDataLoaded] = useState(false)

  const observationError = useSelector(observationSearchError)

  // Pagination process
  const getNextData = () => {
    const hasNext = _.get(observationData, 'link[1].relation', '') === 'next'
    if (hasNext) {
      const nextUrl = _.get(observationData, 'link[1].url', '')
      const params = queryParams(nextUrl)
      const queryFHIR = _.get(params, '_count', '50')
      const { pageContext, pageDirection } = getPageParamsFromUrl(nextUrl)
      dispatch(
        FhirEnhancedActions.observationSearchEnhancedNext(
          patientId,
          `_count=${queryFHIR}`,
          pageContext,
          pageDirection
        )
      )
    } else {
      setAllDataLoaded(true)
    }
  }

  useEffect(() => {
    const hasNext = _.get(data, 'link[1].relation') === 'next'
    setAllDataLoaded(!hasNext)
  }, [data])

  useEffect(
    () => {
      if (uniqEncounterId) {
        uniqEncounterId.forEach(encounterId => {
          const encounterContent = _.get(encounter, encounterId, '')
          const isLoading = _.get(encounterLoading, encounterId, false)
          if (!_.isEmpty(encounterId) && !encounterContent && !isLoading) {
            dispatch(FhirActions.encounterRead(encounterId))
          }
        })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [uniqEncounterId]
  )

  // Handles the submission action.
  // Define start time based on the value of startDate, default to ''.
  // Define end time based on the value of endDate, default to ''.
  const submitHandler = () => {
    const startTime = _.isEmpty(startDate)
      ? ''
      : moment(startDate).utc().format('YYYY-MM-DD')
    const endTime = _.isEmpty(endDate)
      ? ''
      : moment(endDate).utc().format('YYYY-MM-DD')
    const queryStart = startTime ? `${`&date=ge${startTime}`}` : ''
    const queryEnd = endTime ? `${`&date=le${endTime}`}` : ''
    const query = `${queryStart}${queryEnd}`
    dispatch(testResultsActions.setStartDate(startDate))
    dispatch(testResultsActions.setEndDate(endDate))
    dispatch(
      FhirEnhancedActions.observationSearchEnhanced(`${patientId}${query}`)
    )
  }

  const resetFilter = () => {
    setStartDate('')
    setEndDate('')
    if (_.isEmpty(startDate) && _.isEmpty(endDate)) {
      return
    }
    dispatch(testResultsActions.setStartDate(''))
    dispatch(testResultsActions.setEndDate(''))
    setOpenedItemIndex(null)
    dispatch(FhirEnhancedActions.observationSearchEnhancedSuccess(null))
    const cachedData = _.get(cachedTestResults, patientId, null)
    if (cachedData) {
      dispatch(FhirEnhancedActions.observationSearchEnhancedSuccess(cachedData))
    } else {
      dispatch(FhirEnhancedActions.observationSearchEnhanced(`${patientId}`))
    }
  }

  const testHandler = index => {
    if (index === openedItemIndex) {
      setOpenedItemIndex(null)
    } else {
      setOpenedItemIndex(index)
    }
  }

  const onFilter = () => {
    setOpenedItemIndex(null)
    dispatch(FhirEnhancedActions.observationSearchEnhancedSuccess(null))
    submitHandler()
  }

  return (
    <>
      <div style={styles.filterContainer}>
        <div>
          <span style={styles.filterLabel}>{t('filterByDateRange')}</span>
          {isMobile && (
            <Button style={styles.resetButton} onClick={resetFilter}>
              {t('reset')}
            </Button>
          )}
        </div>
        <br />
        <div>
          <Space size={!isMobile ? 'large' : 'small'} align='end'>
            <DatePicker
              value={_.isEmpty(startDate) ? '' : dayjs(startDate, 'MM/DD/YYYY')}
              defaultValue={
                !_.isEmpty(startDate) && dayjs(startDate, 'MM/DD/YYYY')
              }
              minDate={dayjs('12-31-1970', 'MM/DD/YYYY')}
              maxDate={
                _.isEmpty(endDate) ? dayjs() : dayjs(endDate, 'MM/DD/YYYY')
              }
              onChange={(_, dateString) => {
                setStartDate(dateString)
              }}
              label={!isMobile && t('startDate')}
              placeholder={isMobile ? t('startDate') : 'MM/DD/YYYY'}
            />
            <DatePicker
              value={_.isEmpty(endDate) ? '' : dayjs(endDate, 'MM/DD/YYYY')}
              defaultValue={!_.isEmpty(endDate) && dayjs(endDate, 'MM/DD/YYYY')}
              minDate={dayjs(
                _.isEmpty(startDate) ? '12-31-1970' : startDate,
                'MM/DD/YYYY'
              )}
              maxDate={dayjs()}
              onChange={(_, dateString) => {
                setEndDate(dateString)
              }}
              label={!isMobile && t('endDate')}
              placeholder={isMobile ? t('endDate') : 'MM/DD/YYYY'}
            />
            <Button onClick={onFilter} style={styles.submitButton(isMobile)}>
              {!isMobile ? t('submit') : <Arrow style={styles.buttonIcon} />}
            </Button>
            {!isMobile && (
              <Button onClick={resetFilter} style={styles.resetButtonBig}>
                {t('reset')}
              </Button>
            )}
          </Space>
        </div>
      </div>
      <br />
      <div style={styles.container}>
        {loading && _.isEmpty(testsInfoGroupedByDates) && <Loading />}
        {!_.isEmpty(testsInfoGroupedByDates) &&
          testsInfoGroupedByDates.map((test, index) => {
            const testInfo = Object.values(test)[0]
            return (
              !_.every(
                testInfo,
                item => _.isNil(item.result) || item.result === ''
              ) && (
                <Fragment key={index}>
                  <ResultItem
                    test={test}
                    isFirstResult={index === 0}
                    isOpen={index === openedItemIndex}
                    testHandler={() => testHandler(index)}
                  />
                </Fragment>
              )
            )
          })}
        {loading ? null : _.isEmpty(testsInfoGroupedByDates) &&
          !observationError ? (
          <div style={styles.noDataContainer}>
            <NoData name={name} />
          </div>
        ) : (
          _.isEmpty(testsInfoGroupedByDates) &&
          observationError && <SthWentWrong />
        )}
        {!allDataLoaded ? (
          <div style={styles.loadMoreButtonWrapper}>
            <LoadMoreButton
              loading={isObservationNextLoading}
              onClick={getNextData}
            />
          </div>
        ) : null}
      </div>
    </>
  )
}

export default withLocalization(TestResults, localization)
